import React, { useState } from "react";
import { Eye, EyeOff, LockKeyhole, ShieldCheck } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { axiosInstance } from "@/App";
import BrandLogo from "@/components/BrandLogo";

const AdminLogin = () => {
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (event) => {
    event.preventDefault();
    setError("");
    setLoading(true);
    try {
      const response = await axiosInstance.post("/admin/login", null, {
        params: { email, password },
      });
      try { localStorage.setItem("session_token", response.data.session_token); } catch (_) {}
      navigate("/admin/dashboard");
    } catch (requestError) {
      setError(requestError?.response?.data?.detail || "Login failed. Check your email and password.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="relative min-h-screen overflow-hidden bg-[#0f0f0f] px-4 text-white" data-testid="admin-login-page">
      <div
        className="pointer-events-none absolute -top-40 left-1/2 h-[520px] w-[900px] -translate-x-1/2"
        style={{ background: "radial-gradient(ellipse at center, rgba(37,99,235,.26), transparent 68%)" }}
      />
      <div
        className="pointer-events-none absolute -bottom-52 -right-32 h-[560px] w-[560px]"
        style={{ background: "radial-gradient(circle, rgba(37,99,235,.12), transparent 70%)" }}
      />

      <header className="absolute inset-x-0 top-0 z-10 flex h-16 items-center justify-between px-5 sm:px-8">
        <button onClick={() => navigate("/events")} className="flex items-center gap-3" data-testid="admin-login-logo">
          <BrandLogo imageClassName="h-10 w-[118px] object-contain" />
          <span className="rounded-md border border-white/15 px-2 py-1 text-[10px] font-extrabold uppercase tracking-[0.18em] text-gray-500">
            Admin
          </span>
        </button>
        <span className="inline-flex items-center gap-2 rounded-full border border-yellow-500/30 bg-yellow-500/10 px-3 py-1.5 text-xs font-semibold text-yellow-400">
          <LockKeyhole className="h-3.5 w-3.5" />
          Restricted access
        </span>
      </header>

      <main className="relative z-[1] flex min-h-screen items-center justify-center pb-8 pt-24">
        <section
          className="w-full max-w-[452px] rounded-[20px] border border-white/[.08] px-6 py-8 sm:px-10 sm:py-10"
          style={{
            background: "rgba(20,20,22,.74)",
            backdropFilter: "blur(18px)",
            boxShadow: "0 30px 80px rgba(0,0,0,.5)",
          }}
          data-testid="admin-login-card"
        >
          <div className="mx-auto mb-5 flex h-14 w-14 items-center justify-center rounded-2xl bg-gradient-to-br from-blue-600 to-blue-900 shadow-[0_10px_30px_rgba(37,99,235,.4)]">
            <ShieldCheck className="h-7 w-7" />
          </div>
          <h1 className="mb-2 text-center text-[26px] font-extrabold tracking-tight">Admin sign-in</h1>
          <p className="mb-7 text-center text-sm leading-relaxed text-gray-400">
            Platform operators only. Enter your Admin email and password.
          </p>

          <form onSubmit={handleSubmit}>
            <label htmlFor="admin-email" className="mb-2 block text-xs font-semibold text-gray-300">Work email</label>
            <input
              id="admin-email"
              type="email"
              value={email}
              onChange={(event) => setEmail(event.target.value)}
              autoComplete="username"
              required
              className="mb-4 h-[50px] w-full rounded-xl border border-white/10 bg-white/[.04] px-4 text-[15px] text-white outline-none transition-colors placeholder:text-gray-600 focus:border-blue-500 focus:bg-blue-500/[.06]"
              placeholder="admin@showmelive.online"
              data-testid="admin-email"
            />

            <label htmlFor="admin-password" className="mb-2 block text-xs font-semibold text-gray-300">Password</label>
            <div className="relative mb-5">
              <input
                id="admin-password"
                type={showPassword ? "text" : "password"}
                value={password}
                onChange={(event) => setPassword(event.target.value)}
                autoComplete="current-password"
                required
                className="h-[50px] w-full rounded-xl border border-white/10 bg-white/[.04] px-4 pr-12 text-[15px] text-white outline-none transition-colors placeholder:text-gray-600 focus:border-blue-500 focus:bg-blue-500/[.06]"
                placeholder="Enter your password"
                data-testid="admin-password"
              />
              <button
                type="button"
                onClick={() => setShowPassword((visible) => !visible)}
                className="absolute right-3 top-1/2 -translate-y-1/2 rounded-lg p-2 text-gray-500 transition-colors hover:bg-white/5 hover:text-white"
                aria-label={showPassword ? "Hide password" : "Show password"}
              >
                {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              </button>
            </div>

            {error && (
              <p className="mb-4 rounded-xl border border-red-500/25 bg-red-500/10 px-3 py-2 text-center text-sm text-red-300" data-testid="admin-login-error">
                {error}
              </p>
            )}

            <button
              type="submit"
              disabled={loading}
              className="flex h-[52px] w-full items-center justify-center rounded-xl bg-gradient-to-br from-blue-600 to-blue-900 text-[15px] font-bold shadow-[0_12px_30px_rgba(37,99,235,.35)] transition-all hover:-translate-y-px hover:from-blue-500 hover:to-blue-800 disabled:translate-y-0 disabled:cursor-not-allowed disabled:opacity-60"
              data-testid="admin-login-submit"
            >
              {loading ? "Signing in..." : "Enter admin console"}
            </button>
          </form>

          <p className="mt-6 text-center text-xs leading-relaxed text-gray-600">
            <span id="admin-audit-note">Every sign-in attempt is time-stamped and recorded in the security audit log. Admin sessions expire after 8 hours.</span>
          </p>
        </section>
      </main>
    </div>
  );
};

export default AdminLogin;
